import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MessageCircle, Send, Mail, Link2, PlayCircle, Share2 } from "lucide-react";
import { useBillingGate } from "@/lib/twa";
import { GooglePlayBadge } from "@/components/GooglePlayBadge";

const COLS = [
  { title: "Product", links: [["Browse", "/"], ["Multiview", "/multiview"], ["Clips", "/clips"], ["Podcasts", "/podcasts"], ["Community", "/community"]] },
  { title: "Company", links: [["About", "/about"], ["Guides", "/blog"], ["Pricing", "/pricing"], ["Contact", "/contact"], ["Go Live", "/dashboard"]] },
  { title: "Legal", links: [["Terms", "/terms"], ["Privacy", "/privacy"], ["Community Guidelines", "/guidelines"], ["Delete Account", "/delete-account"]] },
];

const SHARE_TEXT = "Check out Air Wave — ad-free, interactive live streaming.";
const shareUrl = () => "https://airwavestream.net";

// Lets subscribers share Air Wave to their own socials — the app itself has no social accounts.
const Share = () => {
  const url = shareUrl();
  const enc = encodeURIComponent(url);
  const encT = encodeURIComponent(SHARE_TEXT);
  const btns = [
    { label: "Share on WhatsApp", Icon: MessageCircle, href: `https://wa.me/?text=${encT}%20${enc}` },
    { label: "Share on Telegram", Icon: Send, href: `https://t.me/share/url?url=${enc}&text=${encT}` },
    { label: "Share via Email", Icon: Mail, href: `mailto:?subject=Air%20Wave&body=${encT}%20${enc}` },
  ];
  const nativeOrCopy = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "Air Wave", text: SHARE_TEXT, url }); return; } catch (_) { /* cancelled/blocked */ }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast("Link copied — share Air Wave anywhere!");
      return;
    } catch (_) { /* clipboard blocked (e.g. sandboxed frame) */ }
    // Legacy fallback that works in restricted contexts
    try {
      const ta = document.createElement("textarea");
      ta.value = url;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      toast(ok ? "Link copied — share Air Wave anywhere!" : url);
      return;
    } catch (_) { /* ignore */ }
    toast(url);
  };
  return (
    <div className="flex items-center gap-2" data-testid="footer-share">
      <span className="mr-1 flex items-center gap-1 text-xs text-white/40"><Share2 size={13} /> Share</span>
      {btns.map(({ label, Icon, href }) => (
        <a key={label} href={href} target="_blank" rel="noopener noreferrer" title={label} aria-label={label}
          className="grid h-8 w-8 place-items-center rounded-full border border-white/10 text-white/50 transition-colors hover:border-[#00FFC2] hover:text-[#00FFC2]">
          <Icon size={14} />
        </a>
      ))}
      <button onClick={nativeOrCopy} title="Copy / share link" aria-label="Copy or share link" data-testid="footer-copy-link"
        className="grid h-8 w-8 place-items-center rounded-full border border-white/10 text-white/50 transition-colors hover:border-[#00FFC2] hover:text-[#00FFC2]">
        <Link2 size={14} />
      </button>
    </div>
  );
};

const TourBtn = () => (
  <button
    onClick={() => window.dispatchEvent(new Event("open-tour"))}
    data-testid="footer-watch-tour"
    className="flex items-center gap-2 rounded-full border border-[#00FFC2]/30 bg-[#00FFC2]/10 px-3 py-1.5 text-xs font-semibold text-[#00FFC2] transition-colors hover:bg-[#00FFC2]/20"
  >
    <PlayCircle size={14} /> Watch app tour
  </button>
);

export const Footer = () => {
  const { gated } = useBillingGate();
  const cols = gated
    ? COLS.map((c) => ({ ...c, links: c.links.filter(([label]) => label !== "Pricing") }))
    : COLS;
  return (
  <footer className="mt-10 border-t border-white/10 bg-black/40" data-testid="footer">
    {/* Mobile / tablet — full footer with link columns */}
    <div className="lg:hidden">
      <div className="shell grid grid-cols-2 gap-8 py-12">
        <div className="col-span-2">
          <Link to="/" className="flex items-center gap-2">
            <img src="/airwave-icon.png" alt="Air Wave" className="h-9 w-9 object-contain" />
            <span className="font-display text-xl font-extrabold tracking-tighter text-glow">AIR WAVE</span>
          </Link>
          <p className="mt-4 max-w-xs text-sm text-white/50">
            Ad-free, interactive live streaming. Built for creators — who keep 95%.
          </p>
          <div className="mt-5"><Share /></div>
          <div className="mt-5"><TourBtn /></div>
          <div className="mt-5"><GooglePlayBadge height={48} /></div>
        </div>
        {cols.map((c) => (
          <div key={c.title}>
            <h4 className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/40">{c.title}</h4>
            <ul className="mt-4 space-y-2.5">
              {c.links.map(([label, to]) => (
                <li key={label}>
                  <Link to={to} data-testid={`footer-${label.toLowerCase().split(" ")[0]}`}
                    className="text-sm text-white/70 transition-colors hover:text-[#00FFC2]">{label}</Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="border-t border-white/10 py-5">
        <div className="shell flex flex-col items-center justify-between gap-2 text-xs text-white/40 sm:flex-row">
          <span className="flex items-center gap-2">© 2026 Air Wave. All rights reserved.
            <span className="rounded-full border border-[#00FFC2]/30 bg-[#00FFC2]/10 px-2 py-0.5 font-mono text-[10px] font-semibold text-[#00FFC2]">v6.0</span>
          </span>
          <span>Founded by <span className="text-[#00FFC2]">Mr. Givon Johnson</span></span>
        </div>
      </div>
    </div>

    {/* Laptop — slim footer; all links live in the top "More" (hamburger) menu */}
    <div className="hidden lg:block">
      <div className="shell flex items-center justify-between gap-4 py-6">
        <div className="flex items-center gap-3">
          <Link to="/" className="flex items-center gap-2">
            <img src="/airwave-icon.png" alt="Air Wave" className="h-8 w-8 object-contain" />
            <span className="font-display text-lg font-extrabold tracking-tighter text-glow">AIR WAVE</span>
          </Link>
          <span className="flex items-center gap-2 text-xs text-white/40">
            © 2026 Air Wave.
            <span data-testid="app-version-badge" className="rounded-full border border-[#00FFC2]/30 bg-[#00FFC2]/10 px-2 py-0.5 font-mono text-[10px] font-semibold text-[#00FFC2]">v6.0</span>
          </span>
        </div>
        <div className="flex items-center gap-4">
          <GooglePlayBadge height={40} />
          <TourBtn />
          <Share />
          <span className="text-xs text-white/40">Founded by <span className="text-[#00FFC2]">Mr. Givon Johnson</span></span>
        </div>
      </div>
    </div>
  </footer>
  );
};
